import React from 'react';
import './su-components.css';

export interface Column<T> {
  key: string;
  header: React.ReactNode;
  render: (row: T, index: number) => React.ReactNode;
  align?: 'left' | 'right';
  /** Fixed column width (px or css length). Omit on the flexible column (it takes the remaining space). */
  width?: number | string;
}

/** One header cell over a run of columns (e.g. "TODAY ₹ L / YoY" over Sales + YoY); those columns' own headers are not shown. */
export interface HeaderGroup {
  label: React.ReactNode;
  span: number;
  tone?: 'amber' | 'violet';
}

export interface RankedTableProps<T> {
  headerGroups?: HeaderGroup[];
  /** Small note rendered inside the card under the table. */
  footer?: React.ReactNode;
  /** Rendered between the title and the table (e.g. the store type colour legend). */
  legend?: React.ReactNode;
  className?: string;
  columns: Column<T>[];
  rows: T[] | null | undefined;
  rowKey: (row: T) => string;
  title?: React.ReactNode;
  onRowClick?: (row: T) => void;
  onViewAll?: () => void;
  viewAllLabel?: string;
  loading?: boolean;
  skeletonRows?: number;
  emptyText?: string;
}

function RankedTable<T>({
  columns, headerGroups, footer, legend, className, rows, rowKey, title, onRowClick, onViewAll, viewAllLabel = 'View All',
  loading, skeletonRows = 5, emptyText = 'No data',
}: RankedTableProps<T>) {
  const showSkeleton = loading && (!rows || rows.length === 0);
  return (
    <div className={`su-card su-tablecard${className ? ` ${className}` : ''}`}>
      {(title || onViewAll) && (
        <div className="su-table-head">
          <h3 className="su-h2">{title}</h3>
          {onViewAll && (
            <button type="button" className="su-link" onClick={onViewAll}>{viewAllLabel}</button>
          )}
        </div>
      )}
      {legend}
      <table className="su-table">
        <colgroup>
          {columns.map((c) => (
            <col key={c.key} style={c.width != null ? { width: c.width } : undefined} />
          ))}
        </colgroup>
        <thead>
          <tr>
            {headerGroups
              ? (() => {
                  const covered = headerGroups.reduce((n, g) => n + g.span, 0);
                  return [
                    ...columns.slice(0, columns.length - covered).map((c) => (
                      <th key={c.key} className={c.align === 'right' ? 'num' : ''}>{c.header}</th>
                    )),
                    ...headerGroups.map((g, i) => (
                      <th key={`g${i}`} colSpan={g.span} className={`su-group su-group-${g.tone ?? 'violet'}`}>{g.label}</th>
                    )),
                  ];
                })()
              : columns.map((c) => (
                  <th key={c.key} className={c.align === 'right' ? 'num' : ''}>{c.header}</th>
                ))}
          </tr>
        </thead>
        <tbody>
          {showSkeleton
            ? Array.from({ length: skeletonRows }).map((_, i) => (
                <tr key={`sk${i}`}>
                  {columns.map((c) => (
                    <td key={c.key}><div className="su-skeleton" style={{ height: 16 }} /></td>
                  ))}
                </tr>
              ))
            : (rows ?? []).map((row, i) => (
                <tr
                  key={rowKey(row)}
                  className={onRowClick ? 'clickable' : ''}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                >
                  {columns.map((c) => (
                    <td key={c.key} className={c.align === 'right' ? 'num su-num' : ''}>{c.render(row, i)}</td>
                  ))}
                </tr>
              ))}
          {!showSkeleton && (!rows || rows.length === 0) && (
            <tr><td colSpan={columns.length} className="su-muted">{emptyText}</td></tr>
          )}
        </tbody>
      </table>
      {footer && <div className="su-table-foot">{footer}</div>}
    </div>
  );
}

export default RankedTable;
